import React, { useMemo, memo, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles, Divider } from "@material-ui/core";
import ReactList from "react-list";
import { useTracker } from "@dpdgroupuk/react-event-tracker";
import { useHistory } from "react-router-dom";

import DrawerToggle from "./DrawerToggle";
import {
  filterRoutes,
  FilterTypes,
  sortRoutesBySectionRoute,
} from "../../../../models/route";
import { useRouteClicks } from "../../../../hooks/useRouteClicks";
import { RouteListTabs } from "../../../../enums/RouteListTabs";

import {
  getDefaultRoutes,
  getDriverRoutes,
  getRoutesList,
  getSectionRoute,
} from "../../../../store/selectors/routes";
import {
  paramsToArray,
  parseQuery,
  stringifyQuery,
} from "../../../../utils/query";
import usePrevious from "../../../../hooks/usePrevious";
import { getCard, getCardSize } from "../../../../models/UI";
import palette from "../../../styles/palette";
import { ROUTES, ROUTES_TABS } from "../../../../constants/analytics";
import { manuallyLockChange } from "../../../../store/actions/route";
import { ListWrapper } from "./ListWrapper";
import { useRouteListContext } from "../../../../hooks/RouteList";
import useLocationQueryParams from "../../../../hooks/useLocationQueryParams";

const colors = [palette.map.key1, palette.map.key2, palette.map.key3];

const useStyles = makeStyles(() => ({
  headerDivider: {
    background: palette.typography.white.neutral,
  },
  divider: {
    height: "0.125rem",
    background: "transparent",
  },
}));

const ListView = memo(
  ({
    routes,
    selectedTab,
    selectedRoute,
    onRouteCodeClick,
    onStatusChange,
  }) => {
    const styles = useStyles();
    const tracker = useTracker();
    const dispatch = useDispatch();
    const [onClick, onDoubleClick] = useRouteClicks();
    const prevRoutes = usePrevious(routes);
    const { listRef, scrollToRoute } = useRouteListContext();

    const scrollListTo = useCallback(
      (scrollToRouteId) => {
        const selectedStopIndex = routes.findIndex(
          (route) => route.routeId === scrollToRouteId
        );
        listRef.current.scrollTo(selectedStopIndex);
      },
      [routes]
    );

    useEffect(() => {
      if (!prevRoutes?.length && routes.length) {
        scrollListTo(selectedRoute);
      }
    }, [routes]);

    useEffect(() => {
      if (scrollToRoute) {
        scrollListTo(scrollToRoute);
      }
    }, [scrollToRoute, selectedTab]);

    const onLockChange = useCallback(
      (route) => {
        dispatch(manuallyLockChange(route));

        tracker.logEvent(
          ROUTES[
            route.routeLockedBy ? "CLICK_UNLOCK_ROUTE" : "CLICK_LOCK_ROUTE"
          ]
        );
      },
      [tracker, dispatch]
    );

    const onOpenLockRoutePopover = useCallback(() => {
      tracker.logEvent(ROUTES.CLICK_ROUTE_OPTIONS_MENU);
    }, [tracker]);

    return (
      <ReactList
        ref={listRef}
        threshold={400}
        length={routes.length}
        itemSizeGetter={(index) =>
          getCardSize(selectedTab, routes[index].defaultRoute)
        }
        type={"variable"}
        itemRenderer={(index, key) => {
          const route = routes[index];
          const Component = getCard(selectedTab, route);

          return (
            <div key={key}>
              <Divider className={styles.divider} />
              <Component
                showLockRoute
                route={route}
                spot={colors[index % 3]}
                active={selectedRoute === route.routeId}
                onClick={() => onClick(route)}
                onDoubleClick={() => onDoubleClick(route)}
                onRouteCodeClick={() => onRouteCodeClick(route)}
                onStatusChange={onStatusChange}
                onLockChange={() => onLockChange(route)}
                onOpenLockRoutePopover={onOpenLockRoutePopover}
              />
            </div>
          );
        }}
      />
    );
  }
);

// TODO: move this component to features
const RouteList = ({
  routeId,
  isRouteDetail,
  onRouteCodeClick,
  filters,
  onStatusChange,
  onListAddItemClick,
}) => {
  const styles = useStyles();
  const tracker = useTracker();
  const history = useHistory();
  const routes = useSelector(getRoutesList);
  const sectionRoute = useSelector((state) =>
    getSectionRoute(state, filters.sectionId)
  );
  const location = useLocationQueryParams();

  const cardType = useMemo(
    () => location?.cardType || RouteListTabs.defRoutes,
    [location]
  );

  // TODO: filtering through query slow down app a lot
  const defaultRoutes = useSelector((state) =>
    getDefaultRoutes(state, filters.sectionId)
  );
  const driverRoutes = useSelector(getDriverRoutes);

  const setCardType = useCallback(
    (value, usePrev) => {
      const { cardType, ...newQuery } = parseQuery(history.location.search);
      history.push({
        search: stringifyQuery({
          ...newQuery,
          cardType: usePrev ? cardType || value : value,
        }),
      });
    },
    [history]
  );

  useEffect(() => {
    setCardType(RouteListTabs.defRoutes, true);
  }, [setCardType, location]);

  useEffect(() => {
    if (isRouteDetail) {
      const { cardType, ...newQuery } = parseQuery(history.location.search);
      history.push({
        search: stringifyQuery({
          ...newQuery,
          cardType: RouteListTabs.detailed,
        }),
      });
    }
  }, [isRouteDetail, history]);

  const filter = useMemo(
    () => paramsToArray(filters, Object.values(FilterTypes)),
    [filters]
  );

  const currentRoutes = useMemo(() => {
    if (isRouteDetail) {
      const selectedRoute = routes[routeId];
      return selectedRoute ? [selectedRoute] : [];
    }
    return filterRoutes(filter, driverRoutes);
  }, [isRouteDetail, filter, driverRoutes, routes, routeId]);

  const data = useMemo(() => {
    if (cardType === RouteListTabs.defRoutes) {
      return sectionRoute ? [sectionRoute, ...defaultRoutes] : defaultRoutes;
    } else if (isRouteDetail) {
      return currentRoutes;
    } else {
      const routesData = sectionRoute
        ? [sectionRoute, ...currentRoutes, ...defaultRoutes]
        : [...currentRoutes, ...defaultRoutes];
      return sortRoutesBySectionRoute(routesData);
    }
  }, [cardType, isRouteDetail, currentRoutes, defaultRoutes, sectionRoute]);

  const onTabChange = useCallback(
    (e, value) => {
      if (e) {
        setCardType(value);
        tracker.logEvent(ROUTES_TABS[value]);
      }
    },
    [setCardType, tracker]
  );

  return (
    <>
      <DrawerToggle
        onListAddItemClick={onListAddItemClick}
        value={cardType}
        onChange={onTabChange}
        isRouteDetail={isRouteDetail}
      />
      <Divider className={styles.headerDivider} />
      <ListWrapper>
        <ListView
          selectedTab={cardType}
          routes={data}
          selectedRoute={routeId}
          onRouteCodeClick={onRouteCodeClick}
          onStatusChange={onStatusChange}
        />
      </ListWrapper>
    </>
  );
};

export default memo(RouteList);
